(function () {
  'use strict';

  const STORAGE_KEY = 'hla:favorites:v1';
  const QUEUE_KEY = 'hla:favorites:captureQueue:v1';
  const CHANGE_EVENT = 'hla:favorites:change';
  const productCache = new Map();
  const state = {
    lastTrigger: null,
  };

  function readJson(key, fallback) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (_) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
      // Storage can be unavailable in private contexts; the UI should still work.
    }
  }

  function favorites() {
    const value = readJson(STORAGE_KEY, {});
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  }

  function emitFavoritesChange(action, item) {
    const detail = { action, item, favorites: favorites() };
    try {
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
    } catch (_) {
      try {
        const event = document.createEvent('CustomEvent');
        event.initCustomEvent(CHANGE_EVENT, false, false, detail);
        window.dispatchEvent(event);
      } catch (__) {
        // Same-window sync is best effort; localStorage remains the source of truth.
      }
    }
  }

  function favoriteKey(card) {
    const productId = card.dataset.productId || card.dataset.productHandle || 'product';
    const variantId = card.dataset.variantId || card.dataset.sku || 'default';
    return `${productId}:${variantId}`;
  }

  function normalizeImage(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    return url;
  }

  function styleRootFromSku(sku) {
    return String(sku || '').split(' ')[0].trim().toUpperCase();
  }

  function imageSequence(url) {
    const match = String(url || '').match(/-(\d+)\.(?:jpe?g|png|webp)(?:\?|$)/i);
    return match ? Number(match[1]) : 99;
  }

  function productImages(product, item) {
    const root = styleRootFromSku(item.sku);
    const normalized = (product?.images || []).map(normalizeImage).filter(Boolean);
    if (!root) return normalized.slice(0, 2);

    const rootImages = normalized.filter(url => {
      const file = decodeURIComponent(url.split('/').pop() || '').toUpperCase();
      if (!file.includes(root)) return false;
      if (!root.endsWith('W') && file.includes(`${root}W`)) return false;
      return true;
    });

    const sorted = (rootImages.length ? rootImages : normalized)
      .slice()
      .sort((a, b) => imageSequence(a) - imageSequence(b));
    return sorted.slice(0, 2);
  }

  function cardPayload(card, product) {
    const variantId = String(card.dataset.variantId || '');
    const variants = product?.variants || [];
    const variant = variants.find(item => String(item.id) === variantId) || variants[0] || {};
    const image = normalizeImage(
      product?.featured_image
      || product?.images?.[0]
      || variant.featured_image?.src
      || card.dataset.cardImage
    );

    return {
      key: favoriteKey(card),
      productId: card.dataset.productId || (product?.id ? String(product.id) : ''),
      variantId: card.dataset.variantId || (variant.id ? String(variant.id) : ''),
      handle: card.dataset.productHandle || product?.handle || '',
      title: product?.title || card.dataset.productTitle || '',
      sku: variant.sku || card.dataset.sku || '',
      url: card.dataset.productUrl || (product?.handle ? `/products/${product.handle}` : '#'),
      image,
      unitPrice: card.dataset.unitPrice || '',
      packPrice: card.dataset.packPrice || '',
      packSize: card.dataset.packSize || '',
      sizeRatio: card.dataset.sizeRatio || '',
      availability: card.dataset.availability || '',
    };
  }

  function getConfig() {
    return {
      customer: window.HLA_CUSTOMER || null,
      captureEndpoint: window.HLA_FAVORITES_CAPTURE_ENDPOINT || '',
    };
  }

  function queueCapture(payload) {
    const queue = readJson(QUEUE_KEY, []);
    if (!Array.isArray(queue)) return;
    queue.push(payload);
    writeJson(QUEUE_KEY, queue.slice(-100));
  }

  function postCapture(endpoint, payload) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  }

  async function flushCaptureQueue() {
    const config = getConfig();
    if (!config.captureEndpoint) return;

    const queue = readJson(QUEUE_KEY, []);
    if (!Array.isArray(queue) || queue.length === 0) return;

    const remaining = [];
    for (const payload of queue) {
      try {
        const response = await postCapture(config.captureEndpoint, payload);
        if (!response.ok) remaining.push(payload);
      } catch (_) {
        remaining.push(payload);
      }
    }
    writeJson(QUEUE_KEY, remaining.slice(-100));
  }

  function emitCapture(action, item) {
    const config = getConfig();
    const payload = {
      customer: config.customer,
      product: {
        id: item.productId,
        handle: item.handle,
      },
      variant: {
        id: item.variantId,
      },
      sku: item.sku,
      timestamp: new Date().toISOString(),
      action,
    };
    if (item.type) payload.type = item.type;
    if (item.source) payload.source = item.source;
    if (item.look) payload.look = item.look;

    if (!config.captureEndpoint) {
      queueCapture(payload);
      return;
    }

    postCapture(config.captureEndpoint, payload)
      .then(response => {
        if (!response.ok) queueCapture(payload);
      })
      .catch(() => queueCapture(payload));
  }

  function updateFavoriteButtons() {
    const saved = favorites();
    document.querySelectorAll('[data-hla-product-card]').forEach(card => {
      const isSaved = Boolean(saved[favoriteKey(card)]);
      card.querySelectorAll('[data-hla-favorite-toggle]').forEach(button => {
        button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
        button.classList.toggle('is-active', isSaved);
        button.setAttribute('aria-label', `${isSaved ? 'Remove saved style' : 'Save style'} ${card.dataset.productTitle || ''}`.trim());
      });
    });

    const count = Object.keys(saved).length;
    document.querySelectorAll('[data-hla-favorites-count]').forEach(node => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then(response => (response.ok ? response.json() : null))
      .catch(() => null);

    productCache.set(handle, request);
    return request;
  }

  function ensureQuickView() {
    let modal = document.getElementById('HlaQuickView');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'HlaQuickView';
    modal.className = 'hla-quick-view';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="hla-quick-view__backdrop" data-hla-qv-close></div>
      <div class="hla-quick-view__dialog" role="dialog" aria-modal="true" aria-labelledby="HlaQuickViewTitle" tabindex="-1">
        <button type="button" class="hla-quick-view__close" data-hla-qv-close aria-label="Close Quick View">&times;</button>
        <div class="hla-quick-view__body" data-hla-qv-body></div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.addEventListener('click', event => {
      if (event.target.closest('[data-hla-qv-close]')) closeQuickView();
    });
    return modal;
  }

  function ensureFavoritesView() {
    let drawer = document.getElementById('HlaFavoritesView');
    if (drawer) return drawer;

    drawer = document.createElement('div');
    drawer.id = 'HlaFavoritesView';
    drawer.className = 'hla-favorites-view';
    drawer.hidden = true;
    drawer.innerHTML = `
      <div class="hla-favorites-view__backdrop" data-hla-favorites-close></div>
      <aside class="hla-favorites-view__panel" role="dialog" aria-modal="true" aria-labelledby="HlaFavoritesTitle" tabindex="-1">
        <div class="hla-favorites-view__header">
          <h2 id="HlaFavoritesTitle">Favorites</h2>
          <button type="button" class="hla-favorites-view__close" data-hla-favorites-close aria-label="Close Favorites">&times;</button>
        </div>
        <div class="hla-favorites-view__content" data-hla-favorites-list></div>
      </aside>
    `;
    document.body.appendChild(drawer);
    drawer.addEventListener('click', event => {
      if (event.target.closest('[data-hla-favorites-close]')) closeFavoritesView();
    });
    return drawer;
  }

  function imageMarkup(item, product) {
    const images = productImages(product, item);
    const list = images.length ? images : [item.image].filter(Boolean);
    if (!list.length) return '<div class="hla-quick-view__image-placeholder"></div>';
    return `
      <div class="hla-quick-view__images">
        ${list.map((src, index) => `
          <img src="${escapeAttr(src)}" alt="${escapeAttr(item.title)}"${index === 0 ? '' : ' loading="lazy"'}>
        `).join('')}
      </div>
    `;
  }

  function detailsMarkup(item) {
    const packLine = item.sizeRatio
      ? `${item.packSize || ''} pcs: ${item.sizeRatio}`.trim()
      : (item.packSize ? `Pack of ${item.packSize}` : '');
    return `
      <dl class="hla-quick-view__facts">
        <div><dt>SKU</dt><dd>${escapeHtml(item.sku || 'Not available')}</dd></div>
        <div><dt>Unit Price</dt><dd>${escapeHtml(item.unitPrice || 'Not available')}</dd></div>
        <div><dt>Pack</dt><dd>${escapeHtml(packLine || item.packPrice || 'Not available')}</dd></div>
        <div><dt>Availability</dt><dd>${escapeHtml(item.availability || 'Not available')}</dd></div>
      </dl>
    `;
  }

  async function openQuickView(card, trigger) {
    if (!card) return;
    const modal = ensureQuickView();
    const body = modal.querySelector('[data-hla-qv-body]');
    state.lastTrigger = trigger || document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('hla-modal-open');
    body.innerHTML = '<div class="hla-quick-view__loading">Loading</div>';

    const product = await fetchProduct(card.dataset.productHandle);
    const item = cardPayload(card, product);
    const saved = Boolean(favorites()[item.key]);
    body.innerHTML = `
      ${imageMarkup(item, product)}
      <div class="hla-quick-view__details">
        <p class="hla-quick-view__eyebrow">${escapeHtml(item.availability || 'Wholesale')}</p>
        <h2 id="HlaQuickViewTitle">${escapeHtml(item.title)}</h2>
        ${detailsMarkup(item)}
        <div class="hla-quick-view__actions">
          <button type="button" class="button button--secondary hla-quick-view__favorite${saved ? ' is-active' : ''}" data-hla-qv-favorite aria-pressed="${saved ? 'true' : 'false'}">
            <span aria-hidden="true" class="hla-card-action__heart"></span>
            <span>${saved ? 'Saved' : 'Save'}</span>
          </button>
          <a class="button button--primary" href="${escapeAttr(item.url)}">View Product</a>
        </div>
      </div>
    `;
    body.querySelector('[data-hla-qv-favorite]')?.addEventListener('click', event => {
      event.preventDefault();
      toggleFavorite(card, item);
      const isSaved = Boolean(favorites()[item.key]);
      event.currentTarget.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      event.currentTarget.classList.toggle('is-active', isSaved);
      event.currentTarget.querySelector('span:last-child').textContent = isSaved ? 'Saved' : 'Save';
    });
    modal.querySelector('.hla-quick-view__dialog')?.focus({ preventScroll: true });
  }

  function closeQuickView() {
    const modal = document.getElementById('HlaQuickView');
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('hla-modal-open');
    if (state.lastTrigger && typeof state.lastTrigger.focus === 'function') {
      state.lastTrigger.focus({ preventScroll: true });
    }
  }

  function saveFavoriteItem(item, action = 'save') {
    if (!item || !item.key) return null;
    const saved = favorites();
    const nextItem = { ...item, savedAt: new Date().toISOString() };
    saved[item.key] = nextItem;
    writeJson(STORAGE_KEY, saved);
    updateFavoriteButtons();
    renderFavoritesList();
    emitFavoritesChange(action, nextItem);
    emitCapture(action, nextItem);
    return nextItem;
  }

  function removeFavoriteItem(key, action = 'remove') {
    if (!key) return null;
    const saved = favorites();
    const item = saved[key];
    if (!item) return null;
    delete saved[key];
    writeJson(STORAGE_KEY, saved);
    updateFavoriteButtons();
    renderFavoritesList();
    emitFavoritesChange(action, item);
    emitCapture(action, item);
    return item;
  }

  function toggleFavorite(card, prebuiltItem) {
    const saved = favorites();
    const key = favoriteKey(card);
    let action = 'remove';
    let item = saved[key] || prebuiltItem || cardPayload(card, null);
    if (saved[key]) {
      delete saved[key];
    } else {
      item = { ...item, savedAt: new Date().toISOString() };
      saved[key] = item;
      action = 'save';
    }
    writeJson(STORAGE_KEY, saved);
    updateFavoriteButtons();
    renderFavoritesList();
    emitFavoritesChange(action, item);
    emitCapture(action, item);
  }

  function renderFavoritesList() {
    const drawer = ensureFavoritesView();
    const container = drawer.querySelector('[data-hla-favorites-list]');
    const items = Object.values(favorites()).sort((a, b) => String(b.savedAt || '').localeCompare(String(a.savedAt || '')));

    if (!items.length) {
      container.innerHTML = '<p class="hla-favorites-view__empty">No saved styles yet.</p>';
      return;
    }

    container.innerHTML = `
      <div class="hla-favorites-view__grid">
        ${items.map(item => `
          <article class="hla-favorites-card" data-hla-favorite-key="${escapeAttr(item.key)}">
            <a href="${escapeAttr(item.url || '#')}" class="hla-favorites-card__image">
              ${item.image ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.title)}" loading="lazy">` : ''}
            </a>
            <div class="hla-favorites-card__body">
              <h3><a href="${escapeAttr(item.url || '#')}">${escapeHtml(item.title)}</a></h3>
              ${item.type === 'look'
                ? `<p>${escapeHtml([item.look?.line, item.look?.itemCount ? `${item.look.itemCount} pieces` : '', item.look?.subtotal].filter(Boolean).join(' / '))}</p>`
                : `<p>${escapeHtml(item.sku || '')}</p><p>${escapeHtml([item.unitPrice, item.availability].filter(Boolean).join(' / '))}</p>`}
              <button type="button" class="link hla-favorites-card__remove" data-hla-favorite-remove>Remove</button>
            </div>
          </article>
        `).join('')}
      </div>
    `;
  }

  function openFavoritesView() {
    const drawer = ensureFavoritesView();
    renderFavoritesList();
    drawer.hidden = false;
    document.documentElement.classList.add('hla-modal-open');
    drawer.querySelector('.hla-favorites-view__panel')?.focus({ preventScroll: true });
  }

  function closeFavoritesView() {
    const drawer = document.getElementById('HlaFavoritesView');
    if (!drawer) return;
    drawer.hidden = true;
    document.documentElement.classList.remove('hla-modal-open');
  }

  function installHeaderButton() {
    const icons = document.querySelector('.header__icons');
    if (!icons || icons.querySelector('[data-hla-favorites-open]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'header__icon header__icon--hla-favorites link focus-inset';
    button.setAttribute('aria-label', 'Favorites');
    button.setAttribute('data-hla-favorites-open', '');
    button.innerHTML = '<span class="hla-header-heart" aria-hidden="true"></span><span class="visually-hidden">Favorites</span><span class="hla-favorites-count" data-hla-favorites-count hidden>0</span>';
    icons.insertBefore(button, icons.firstChild);
  }

  function bindCards() {
    document.querySelectorAll('[data-hla-product-card]').forEach(card => {
      if (card.dataset.hlaActionsBound === 'true') return;
      card.dataset.hlaActionsBound = 'true';

      // Dawn's full-card overlay link (.full-unstyled-link) lives in a .card__content that
      // paints after .card__inner, so it stacks ABOVE the .hla-card-actions nested inside
      // .card__inner — real clicks land on the link and navigate to the PDP instead of the
      // Quick View / Favorites buttons (z-index can't win: it's a sibling paint-order trap).
      // Reparent the actions to the .card-wrapper (position:relative) so they paint last and
      // receive clicks, while the product image stays under the link (image→PDP preserved).
      card.querySelectorAll('.hla-card-actions').forEach(actions => {
        if (actions.parentElement !== card) card.appendChild(actions);
      });

      card.querySelectorAll('[data-hla-quick-view]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          openQuickView(card, button);
        });
      });

      card.querySelectorAll('[data-hla-favorite-toggle]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          toggleFavorite(card);
        });
      });
    });
  }

  function bindDocument() {
    document.addEventListener('click', event => {
      if (event.target.closest('[data-hla-favorites-open]')) {
        event.preventDefault();
        openFavoritesView();
        return;
      }
      const remove = event.target.closest('[data-hla-favorite-remove]');
      if (remove) {
        event.preventDefault();
        const card = remove.closest('[data-hla-favorite-key]');
        const key = card?.dataset.hlaFavoriteKey;
        if (!key) return;
        removeFavoriteItem(key);
      }
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        closeQuickView();
        closeFavoritesView();
      }
    });
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, char => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }[char]));
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#096;');
  }

  function init() {
    installHeaderButton();
    ensureQuickView();
    ensureFavoritesView();
    bindCards();
    bindDocument();
    flushCaptureQueue();
    window.addEventListener('online', flushCaptureQueue);
    updateFavoriteButtons();
    renderFavoritesList();
    window.HLA_CARD_ACTIONS = {
      emitCapture,
      favorites,
      flushCaptureQueue,
      isSaved: key => Boolean(favorites()[key]),
      openFavoritesView,
      openQuickViewItem: (item, trigger) => {
        const card = {
          dataset: {
            productId: item.productId || '',
            productHandle: item.handle || '',
            productTitle: item.title || '',
            productUrl: item.url || '',
            variantId: item.variantId || '',
            sku: item.sku || '',
            unitPrice: item.unitPrice || '',
            packPrice: item.packPrice || '',
            packSize: item.packSize || '',
            sizeRatio: item.sizeRatio || '',
            availability: item.availability || '',
            cardImage: item.image || '',
          },
        };
        return openQuickView(card, trigger);
      },
      refresh: () => {
        bindCards();
        updateFavoriteButtons();
      },
      removeItem: removeFavoriteItem,
      saveItem: saveFavoriteItem,
      updateFavoriteButtons,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
